"use client";

import { useQueryClient } from "@tanstack/react-query";

import {
  getMatchesMatchidChampionPoolsQueryKey,
  useGetMatchesMatchidChampionPools,
} from "@/shared/api/generated-kubb/hooks/useGetMatchesMatchidChampionPools";
import { useGetTournamentsIdChampionPools } from "@/shared/api/generated-kubb/hooks/useGetTournamentsIdChampionPools";
import { usePostMatchesMatchidChampionPools } from "@/shared/api/generated-kubb/hooks/usePostMatchesMatchidChampionPools";
import { useGetRiotDdragonChampions } from "@/shared/api/generated-kubb/hooks/useGetRiotDdragonChampions";

/**
 * KAN-1676: пул одной стороны одной игры — как его отдаёт
 * `GET /matches/{matchId}/champion-pools`.
 */
export interface ChampionPoolSide {
  side: string;
  championIds: string[];
}

export interface ChampionPoolGame {
  gameNumber: number;
  poolSize: number;
  ruleVersion: number;
  createdAt: string;
  pools: ChampionPoolSide[];
}

export interface MatchChampionPools {
  matchId: string;
  games: ChampionPoolGame[];
}

/** Ключ инвалидации сводки турнира (бейджи в сетке) — общий с сеткой. */
export const tournamentChampionPoolsQueryKey = (tournamentId: string) =>
  [{ url: "/tournaments/:id/champion-pools", params: { id: tournamentId } }] as const;

/**
 * Пул матча. Один запрос на страницу матча; после публикации инвалидируется.
 * Kubb-хук отдаёт ResponseConfig — наружу достаём только полезный payload.
 */
export function useMatchChampionPools(
  matchId: string,
  options?: { refetchInterval?: number; enabled?: boolean },
) {
  const query = useGetMatchesMatchidChampionPools(matchId, {
    query: {
      ...(options?.refetchInterval ? { refetchInterval: options.refetchInterval } : {}),
      ...(options?.enabled === false ? { enabled: false } : {}),
    },
  });
  const data = query.data?.data as MatchChampionPools | undefined;

  return {
    games: data?.games ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/**
 * Публикация пула на игру. Идемпотентна на бэке: повторный вызов на ту же игру
 * вернёт сохранённый пул, поэтому UI после успеха просто перечитывает данные.
 *
 * ВАЖНО: gameNumber передаём всегда явно — вызов без него выдаёт СЛЕДУЮЩУЮ игру,
 * и двойное нажатие выдало бы игру 2 вместо повторного чтения игры 1.
 */
export function usePublishChampionPool(matchId: string, tournamentId?: string) {
  const queryClient = useQueryClient();
  const mutation = usePostMatchesMatchidChampionPools();

  const publish = (
    gameNumber: number,
    poolSize?: number,
    options?: { onSuccess?: () => void; onError?: (error: unknown) => void },
  ) =>
    mutation.mutate(
      { matchId, params: { gameNumber, poolSize } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getMatchesMatchidChampionPoolsQueryKey(matchId),
          });
          if (tournamentId) {
            void queryClient.invalidateQueries({
              queryKey: tournamentChampionPoolsQueryKey(tournamentId),
            });
          }
          options?.onSuccess?.();
        },
        onError: (error) => options?.onError?.(error),
      },
    );

  return {
    publish,
    isPublishing: mutation.isPending,
  };
}

export interface TournamentPoolSummary {
  matchId: string;
  publishedGames: number;
  poolSize: number;
}

/**
 * Сводка пулов турнира — один запрос на страницу сетки/списка. Узлы сетки получают
 * готовый Map, поэтому N матчей не превращаются в N запросов.
 */
export function useTournamentPoolSummary(tournamentId: string | undefined) {
  const query = useGetTournamentsIdChampionPools(tournamentId, {
    query: { enabled: Boolean(tournamentId) },
  });
  const rows = (query.data?.data as TournamentPoolSummary[] | undefined) ?? [];

  return {
    summary: new Map(rows.map((row) => [row.matchId, row])),
    isLoading: query.isLoading,
  };
}


/**
 * Локализованные имена чемпионов (DDragon, ru_RU на бэке) — для копирования пула
 * в Discord и подписей иконок. Справочник меняется раз в патч, поэтому держим его
 * в кэше сутки.
 */
export function useDdragonChampionNames() {
  const query = useGetRiotDdragonChampions({
    query: { staleTime: 24 * 60 * 60 * 1000 },
  });
  const rows = (query.data?.data ??
    []) as Array<{ id?: string; name?: string; attackRange?: number }>;

  const byId = new Map<string, string>();
  for (const row of rows) {
    if (row?.id) byId.set(row.id, row.name ?? row.id);
  }

  return byId;
}
